/**
 * Hook for accessing the 5-level Strategic Framework
 * 
 * Provides access to:
 * - Level 1: Strategic Objectives (Scale, Quality, Culture, Impact)
 * - Level 2: Strategic Initiatives
 * - Level 3: Operational Objectives
 * 
 * Usage:
 * const { strategicObjectives, isLoading } = useStrategicFramework()
 */

import { useQuery } from '@tanstack/react-query'
import { 
  performanceService, 
  type StrategicObjective,
  type StrategicInitiative,
  type OperationalInitiative,
  type StrategicCascade
} from '@/services/api/performanceService'

/**
 * Get all strategic objectives (Level 1)
 */
export function useStrategicObjectives(activeOnly: boolean = true) {
  return useQuery({
    queryKey: ['strategic-objectives', activeOnly],
    queryFn: () => performanceService.getStrategicObjectives(activeOnly),
    staleTime: 10 * 60 * 1000, // 10 minutes - strategic data changes infrequently
  })
}

/**
 * Get a specific strategic objective with full hierarchy
 */
export function useStrategicObjective(
  objectiveId: number | null,
  includeHierarchy: boolean = true
) {
  return useQuery({
    queryKey: ['strategic-objective', objectiveId, includeHierarchy],
    queryFn: () => performanceService.getStrategicObjective(objectiveId!, includeHierarchy),
    enabled: !!objectiveId,
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Get all strategic initiatives (Level 2)
 */
export function useStrategicInitiatives(
  strategicObjectiveId?: number,
  status?: string
) {
  return useQuery({
    queryKey: ['strategic-initiatives', strategicObjectiveId, status],
    queryFn: () => performanceService.getStrategicInitiatives(strategicObjectiveId, status),
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Get all operational objectives (Level 3)
 */
export function useOperationalInitiatives(
  strategicInitiativeId?: number,
  activeOnly: boolean = true
) {
  return useQuery({
    queryKey: ['operational-initiatives', strategicInitiativeId, activeOnly],
    queryFn: () => performanceService.getOperationalInitiatives(strategicInitiativeId, activeOnly),
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Get complete strategic cascade (all 3 levels)
 */
export function useStrategicCascade(activeOnly: boolean = true) {
  return useQuery({
    queryKey: ['strategic-cascade', activeOnly],
    queryFn: () => performanceService.getStrategicCascade(activeOnly),
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Get strategic objectives for dropdown selection
 */
export function useLookupStrategicObjectives() {
  return useQuery({
    queryKey: ['lookup-strategic-objectives'],
    queryFn: () => performanceService.lookupStrategicObjectives(),
    staleTime: 15 * 60 * 1000, // 15 minutes - lookup data is very stable
  })
}

/**
 * Get strategic initiatives for dropdown selection
 */
export function useLookupStrategicInitiatives(strategicObjectiveId?: number) {
  return useQuery({
    queryKey: ['lookup-strategic-initiatives', strategicObjectiveId],
    queryFn: () => performanceService.lookupStrategicInitiatives(strategicObjectiveId),
    staleTime: 15 * 60 * 1000,
  })
}

/**
 * Get operational objectives for dropdown selection
 */
export function useLookupOperationalInitiatives(strategicInitiativeId?: number) {
  return useQuery({
    queryKey: ['lookup-operational-initiatives', strategicInitiativeId],
    queryFn: () => performanceService.lookupOperationalInitiatives(strategicInitiativeId),
    staleTime: 15 * 60 * 1000,
  })
}

/**
 * Combined hook for all strategic framework data with dropdown options
 */
export function useStrategicFramework() {
  const { data: strategicObjectives, isLoading: isLoadingObjectives } = useStrategicObjectives()
  const { data: cascade, isLoading: isLoadingCascade } = useStrategicCascade()
  
  // Lookup data for dropdowns
  const { data: objectivesLookup, isLoading: isLoadingLookup } = useLookupStrategicObjectives()
  
  return {
    // Full data
    strategicObjectives: strategicObjectives || [],
    cascade,
    
    // Lookup data for dropdowns
    objectivesLookup: objectivesLookup || [],
    
    // Loading states
    isLoading: isLoadingObjectives || isLoadingCascade || isLoadingLookup,
    isLoadingObjectives,
    isLoadingCascade,
    
    // Helper functions
    getObjectiveByCode: (code: string) => 
      strategicObjectives?.find(obj => obj.code === code),
    getObjectiveByName: (name: string) => 
      strategicObjectives?.find(obj => obj.name === name),
  }
}

/**
 * Hook for cascaded selectors (Level 1 → Level 2 → Level 3)
 * Handles the dependency chain for strategic context selection
 */
export function useCascadedSelectors() {
  const [selectedObjectiveId, setSelectedObjectiveId] = React.useState<number | null>(null)
  const [selectedInitiativeId, setSelectedInitiativeId] = React.useState<number | null>(null)
  
  // Level 1: Strategic Objectives
  const { data: objectives } = useLookupStrategicObjectives()
  
  // Level 2: Strategic Initiatives (filtered by selected objective)
  const { data: initiatives } = useLookupStrategicInitiatives(selectedObjectiveId || undefined)
  
  // Level 3: Operational Objectives (filtered by selected initiative)
  const { data: operationalInitiatives } = useLookupOperationalInitiatives(selectedInitiativeId || undefined)
  
  // Reset initiative when objective changes
  const handleObjectiveChange = (objectiveId: number | null) => {
    setSelectedObjectiveId(objectiveId)
    setSelectedInitiativeId(null) // Reset dependent selection
  }
  
  return {
    // Data
    objectives: objectives || [],
    initiatives: initiatives || [],
    operationalInitiatives: operationalInitiatives || [],
    
    // Selected IDs
    selectedObjectiveId,
    selectedInitiativeId,
    
    // Selection handlers
    setSelectedObjectiveId: handleObjectiveChange,
    setSelectedInitiativeId,
    
    // Helper to get full selection
    getSelection: () => ({
      strategic_objective_id: selectedObjectiveId,
      strategic_initiative_id: selectedInitiativeId,
    }),
    
    // Helper to clear all selections
    clearSelections: () => {
      setSelectedObjectiveId(null)
      setSelectedInitiativeId(null)
    },
  }
}

// Import React for useState in cascaded selectors
import React from 'react'
